import { jwtDecode } from "jwt-decode"
import { stripEnglishPrefix } from "./authLanguageRoutes"
import {
  isProfileMarketPortalUser,
  normalizeProfileAccountRole,
} from "./modulePermissionSidebar"

/** Post-login path for organization plan owners (merchant portal team UI). */
export const ORGANIZATION_HOME_PATH = "/organization"

function decodeToken(token) {
  if (!token) return null
  try {
    return jwtDecode(token)
  } catch {
    return null
  }
}

/** Unwrap nested profile payloads from GET /account/profile/. */
export function normalizeProfileUser(raw) {
  if (!raw || typeof raw !== "object") return null
  if (raw.user && typeof raw.user === "object") return raw.user
  if (raw.result && typeof raw.result === "object") return raw.result
  return raw
}

/** Profile is linked to a furniture-merchant business (not org-only plan owner). */
export function profileHasBusiness(user) {
  const profile = normalizeProfileUser(user)
  if (!profile) return false

  if (profile.has_business === true || profile.is_business_user === true) {
    return true
  }

  if (profile.store_data === true) {
    return true
  }

  const rawId =
    profile.business_id ?? profile.businessId ?? profile.business
  if (rawId != null && typeof rawId !== "object") {
    const s = String(rawId).trim()
    if (s !== "" && s !== "null" && s !== "undefined") return true
  }

  if (profile.business && typeof profile.business === "object") {
    const bid = profile.business.id ?? profile.business.business_id
    if (bid != null && String(bid).trim() !== "") return true
  }

  const storeId = profile.store_id ?? profile.storeId
  return storeId != null && String(storeId).trim() !== ""
}

/** Org plan owner: profile OWNER with no merchant business attached. */
export function isOrganizationOnlyOwnerProfile(user) {
  const profile = normalizeProfileUser(user)
  if (!profile) return false
  const profileRole = normalizeProfileAccountRole(profile.role)
  return profileRole === "OWNER" && !profileHasBusiness(profile)
}

/** JWT signals furniture-merchant / market portal / super admin. */
export function jwtIndicatesMerchantPortal(token) {
  const decoded = decodeToken(token)
  if (!decoded) return false

  const hr = String(decoded.highest_role ?? "").toUpperCase()
  const mr = String(decoded.market_role ?? "")
  if (hr === "SUPERADMIN" || hr === "OWNER" || hr === "ADMIN" || hr === "VIEWER") {
    return true
  }
  if (mr === "1" || mr === "2" || mr === "3") return true

  if (decoded.is_superuser === true || decoded.is_super_user === true) {
    return true
  }

  const jwtBusiness = decoded.business_id ?? decoded.business
  if (jwtBusiness != null && String(jwtBusiness).trim() !== "") return true

  return false
}

function isProfileSuperuser(profile) {
  if (!profile) return false
  return (
    profile.is_superuser === true ||
    profile.is_super_user === true ||
    profile.market_role_permissions?.is_superuser === true
  )
}

/**
 * GET /account/profile/ `is_org_owner` when present; otherwise `null` (use legacy heuristics).
 */
export function profileOrgOwnerFlag(user) {
  const profile = normalizeProfileUser(user)
  if (!profile || !Object.prototype.hasOwnProperty.call(profile, "is_org_owner")) {
    return null
  }
  return profile.is_org_owner === true
}

export function profileIndicatesMerchantPortalUser(user) {
  const profile = normalizeProfileUser(user)
  if (!profile) return false
  if (profileHasBusiness(profile)) return true
  if (isProfileMarketPortalUser(profile)) return true
  if (isProfileSuperuser(profile)) return true
  if (profileOrgOwnerFlag(profile) === true) return true
  const r = normalizeProfileAccountRole(profile.role)
  return r === "OWNER" || r === "ADMIN" || r === "VIEWER" || r === "SUPERADMIN"
}

/** Downgraded org / personal plan: explicit not-org-owner and no merchant portal signals on profile. */
function isOrphanPersonalPlanProfile(profile) {
  if (!profile) return false
  if (profileOrgOwnerFlag(profile) !== false) return false
  if (profileHasBusiness(profile)) return false
  if (isProfileMarketPortalUser(profile)) return false
  if (isProfileSuperuser(profile)) return false
  const r = normalizeProfileAccountRole(profile.role)
  if (r) return false
  return true
}

/**
 * Personal-plan / downgraded org login with no merchant portal entitlement.
 */
export function isNoActiveMerchantPortalAccount(user, token, isOrgOwner) {
  return (
    !hasMerchantPortalAccess(user, token, isOrgOwner) &&
    isOrphanPersonalPlanProfile(normalizeProfileUser(user))
  )
}

/**
 * Merchant portal access. Profile store/merchant/superadmin signals win over JWT.
 * Stale JWT after org downgrade is ignored for orphan personal-plan profiles only.
 */
export function hasMerchantPortalAccess(user, token, isOrgOwner) {
  if (canAccessOrganizationRoutes(user, isOrgOwner)) return true
  if (profileIndicatesMerchantPortalUser(user)) return true

  const profile = normalizeProfileUser(user)
  if (jwtIndicatesMerchantPortal(token)) {
    if (profile && isOrphanPersonalPlanProfile(profile)) return false
    return true
  }

  return false
}

/**
 * Skip GET /account/organization/ for furniture-merchant and market-portal accounts.
 * Only org-plan owners (profile OWNER, no business, not market portal) are probed.
 */
export function shouldSkipOrganizationProbe(user, token) {
  const profile = normalizeProfileUser(user)

  const orgOwnerFlag = profileOrgOwnerFlag(profile)
  if (orgOwnerFlag !== null) return true

  if (profileHasBusiness(profile)) return true
  if (isProfileMarketPortalUser(profile)) return true
  if (isProfileSuperuser(profile)) return true
  if (jwtIndicatesMerchantPortal(token)) return true
  if (isOrganizationOnlyOwnerProfile(profile)) return false

  const profileRole = profile ? normalizeProfileAccountRole(profile.role) : ""
  if (profileRole === "ADMIN" || profileRole === "VIEWER") return true

  return false
}

/** True while org probe must finish before showing merchant vs org portal UI. */
export function shouldWaitForOrganizationProbe(user, token, isOrgOwner, probeLoading) {
  const orgOwnerFlag = profileOrgOwnerFlag(normalizeProfileUser(user))
  if (orgOwnerFlag === false) return false
  if (orgOwnerFlag === true) return isOrgOwner !== true
  if (shouldSkipOrganizationProbe(user, token)) return false
  return probeLoading || isOrgOwner === null
}

/** Merchant portal: user may open /organization/* (profile flag or successful probe). */
export function canAccessOrganizationRoutes(user, isOrgOwner) {
  const orgOwnerFlag = profileOrgOwnerFlag(normalizeProfileUser(user))
  if (orgOwnerFlag === true) return true
  if (orgOwnerFlag === false) return false
  return isOrgOwner === true
}

export function isOrganizationRoute(pathname) {
  return String(stripEnglishPrefix(pathname) || "").startsWith(
    ORGANIZATION_HOME_PATH,
  )
}

/**
 * Org owner without a merchant business — team portal only (no stores/products nav).
 * Merchants who also own an org keep the full merchant sidebar + Organization section.
 */
export function isOrgOnlyOwnerUser(user, isOrgOwner, token) {
  if (!canAccessOrganizationRoutes(user, isOrgOwner)) return false

  const profile = normalizeProfileUser(user)
  if (profileHasBusiness(profile)) return false
  if (isProfileMarketPortalUser(profile)) return false
  if (isProfileSuperuser(profile)) return false

  const decoded = decodeToken(token)
  const hr = String(decoded?.highest_role ?? "").toUpperCase()
  if (hr === "SUPERADMIN" || hr === "ADMIN") return false

  const mr = String(decoded?.market_role ?? "")
  if (mr === "1" || mr === "2" || mr === "3") return false

  return true
}
